import { useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, errorMessage, saveBlob } from '../lib/api';
import { fmtDateTime } from '../lib/format';
import { ErrorAlert, PageHeader, Spinner } from '../components/ui';
import { FullLogo } from '../components/Layout';
import { LanguageSwitcher, useT } from '../i18n';

interface OfflineResponse {
  type: string;
  license_key: string;
  product: string;
  certificate: string;
  issued_at: number;
}

/**
 * Subida del fichero de solicitud de activación offline y descarga de la respuesta firmada.
 * `endpoint` es el admin (autenticado) o el portal público de autoservicio.
 */
function OfflineUploader({ endpoint, auth }: { endpoint: string; auth: boolean }) {
  const t = useT();
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ response: OfflineResponse; machine: string | null; fileName: string } | null>(null);

  const handle = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      let request: Record<string, unknown>;
      try {
        request = JSON.parse(await file.text()) as Record<string, unknown>;
      } catch {
        throw new Error(t('offline.invalidJson'));
      }
      if (request.type !== 'voxel-offline-activation-request') throw new Error(t('offline.notRequest'));
      const response = await api.post<OfflineResponse>(endpoint, request, { auth });
      const fileName = `${String(request.product)}-${String(request.license_key).slice(-4)}-${t('offline.fileSuffix')}.lic`;
      setResult({ response, machine: (request.machine_name as string) ?? null, fileName });
      saveBlob(new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' }), fileName);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    void handle(e.dataTransfer.files[0]);
  };

  return (
    <>
      <ErrorAlert error={error} />
      <label className={`dropzone${over ? ' over' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}>
        <input type="file" accept=".req,.json,application/json" style={{ display: 'none' }} onChange={(e) => void handle(e.target.files?.[0])} />
        {busy ? <Spinner /> : (
          <>
            <strong>{t('offline.dropTitle')}</strong>
            <div className="muted" style={{ marginTop: 6 }}>{t('offline.dropHint')}</div>
          </>
        )}
      </label>
      {result && (
        <div className="alert success" style={{ marginTop: 16 }}>
          <strong>{t('offline.done')}</strong>{' '}
          {t('offline.doneDetail', {
            file: result.fileName,
            machine: result.machine ?? t('offline.unknownMachine'),
            key: result.response.license_key,
            date: fmtDateTime(new Date(result.response.issued_at * 1000).toISOString()),
          })}
          <div style={{ marginTop: 8 }}>
            <button className="btn btn-sm" onClick={() => saveBlob(new Blob([JSON.stringify(result.response, null, 2)], { type: 'application/json' }), result.fileName)}>
              {t('offline.downloadAgain')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Steps() {
  const t = useT();
  return (
    <ol className="muted" style={{ lineHeight: 1.7, paddingLeft: 18 }}>
      <li>{t('offline.step1')}</li>
      <li>{t('offline.step2')}</li>
      <li>{t('offline.step3')}</li>
    </ol>
  );
}

export function OfflinePage() {
  const t = useT();
  return (
    <>
      <PageHeader title={t('offline.title')} subtitle={t('offline.subtitle')} />
      <div className="grid grid-2">
        <section className="card">
          <OfflineUploader endpoint="/v1/admin/offline-activations" auth />
        </section>
        <section className="card">
          <h2>{t('offline.howTitle')}</h2>
          <Steps />
          <p className="muted">{t('offline.howNote')}</p>
          <Link to="/portal/activacion-offline">{t('offline.portalLink')}</Link>
        </section>
      </div>
    </>
  );
}

/** Portal público (sin login) para que el cliente suba su solicitud. Admite `?lang=en`. */
export function PublicOfflinePortal() {
  const t = useT();
  return (
    <div className="login-page">
      <div className="login-card" style={{ width: 'min(560px, 100%)' }}>
        <div className="login-brand">
          <div className="login-brand-row">
            <FullLogo />
            <LanguageSwitcher />
          </div>
          <div className="login-brand-title">{t('offline.portalTitle')}</div>
        </div>
        <Steps />
        <OfflineUploader endpoint="/v1/offline/activate" auth={false} />
      </div>
    </div>
  );
}
